import {ServiceBase} from '../ServiceBase';
import {IDeepLinkService} from './deep-link.service.interface';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {Check} from '@sparkware/app-utils';
import { observable, reaction, runInAction, makeObservable } from 'mobx';
import {IReactionPublic} from 'mobx';
import {DEEP_LINK_QUERY_PARAM} from './legacy/deep-link-parsers/deep-link-parser.service';
import {ICasinoDeepLinkAction} from "./casino-deep-link-action.interface";



/**
  Deep link can come from
    1. route
    2. SDK event
    3. direct call window.orbit.deepLink
    4. post message
 */

export class DeepLinkService extends ServiceBase implements IDeepLinkService {

  constructor(services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);

    this._subscribeToCurrentUriReaction(true);

    this.services.sdk.events.onDeepLinkReceived(this._executeDeepLinkFromSdkEvent);
    this.services.window.subscribeTo.postMessage(this._executeDeepLinkFromPostMessage);
  }


  private _subscribeToCurrentUriReaction(fireImmediately: boolean): void{
      reaction(() => this.services.location.currentUri,
          (arg, prev, reaction) => this._executeDeepLinkFromCurrentRoute(reaction),
          {
              fireImmediately: fireImmediately
          });
  }

  @observable.ref
  lastTimeDeepLinkWasExecuted: number;

  private _isExecuting: boolean = false;
  get isExecuting(): boolean {
    return this._isExecuting;
  }


  private async _waitForAutologin(): Promise<void> {
    try {
      await this.services.user.waitForAutoLogin();
    } catch (err) {
      // we will continue the deep link flow if waiting for auto login to complete fails.
      // if there are any issues with the auto login probably the login screen will pop up
      // depending on the deep link action
      this.services.logger.error('Waiting for auto login to complete failed', err);
    }
  }

  private async _executeAction(action: ICasinoDeepLinkAction): Promise<void> {
    if(this._isExecuting) {
      this.services.logger.error(`You tried to execute a deep link while another one is in progress. The following deep link will be ignored: ${JSON.stringify(action)}`);
      return;
    }
    this._isExecuting = true;

    try {
      await action.execute();
    } finally {
      this._isExecuting = false;
      runInAction(() => this.lastTimeDeepLinkWasExecuted = Date.now())
    }

  }



  private async _executeDeepLinkFromCurrentRoute(reactionDisposer: IReactionPublic): Promise<void> {


    try {
        reactionDisposer.dispose(); // we unsubscribe from this.services.location.currentUri reaction so if the currentUri changes inside the waitForAutoLogin or for any other reasons it will not trigger another deep link execution

        await this._waitForAutologin();

        // In case there is an auto login we want to create the deep link action after the auto login finishes
        // so any code that looks for isAuthorized during the action creation to find it on true
        let action = this.services.deepLinkActionsFactory.createActionFromCurrentRoute();
        if(!action) {
            return;
        }

        this.services.logger.debug('Execute deep link from current route: ' + this.services.location.currentUri.toString());

        const urlWithoutHash = this.services.location.currentUri.removeSearchParam(DEEP_LINK_QUERY_PARAM).getUrlWithoutHash();
        await this.services.location.replace(urlWithoutHash);

        await this._executeAction(action);

    } finally {
        this._subscribeToCurrentUriReaction(false);
    }


  }

  private _executeDeepLinkFromSdkEvent = async (deepLinkObject: any): Promise<void> => {

    await this._waitForAutologin();

    let action = this.services.deepLinkActionsFactory.createActionFromDeepLinkObject(deepLinkObject);
    if(action) {
      this.services.logger.debug('Execute deep link object in _executeDeepLinkFromSdkEvent: ' + JSON.stringify(deepLinkObject));
      return this._executeAction(action);
    } else if (Check.isNullOrUndefined(deepLinkObject)){
      this.services.logger.warning(`${deepLinkObject} deep link object received from SDK DEEPLINK_RECEIVED event`);
    } else {
      this.services.logger.error(`Received deep link object from the SDK DEEPLINK_RECEIVED is not a valid deep link object. Received data: ${JSON.stringify(deepLinkObject)}`);
    }
  };

  async executeDeepLink(deepLinkObject: any, navigate?: any): Promise<void> {
    if(!Check.isObject(deepLinkObject)) {
      this.services.logger.error(`_executeDeepLinkFromDirectCall was called with a deepLinkObject argument that is not an object`);
      return;
    }
    if(Check.isEmpty(deepLinkObject)) {
      this.services.logger.error(`_executeDeepLinkFromDirectCall was called with an empty deep deepLinkObject argument`);
      return;
    }

    this.services.logger.debug('Execute deep link object in executeDeepLink: ' + JSON.stringify(deepLinkObject));

    await this._waitForAutologin();

    let action = this.services.deepLinkActionsFactory.createActionFromDeepLinkObject(deepLinkObject);
    if(action) {
      if(Check.isString(navigate)) {
        this.services.location.push(navigate as string);
      } else if(Check.isArrayOfStrings(navigate)) {
        if(navigate.length > 0) {
          await this.services.location.push('/' + (navigate as Array<string>).join('/'));
        }
      } else if(!Check.isNullOrUndefined(navigate)) {
        this.services.logger.error(`expecting _executeDeepLinkFromDirectCall to be called with a navigate parameter that is string or an array of strings but it was ${JSON.stringify(navigate)}`);
      }

      await this._executeAction(action);
    } else {
      this.services.logger.error(`_executeDeepLinkFromDirectCall was called with an invalid deep link object ${JSON.stringify(deepLinkObject)}`);
    }
  };

  private _executeDeepLinkFromPostMessage = async (postEvent: any): Promise<void> => {
    let messageData = postEvent && postEvent.data;
    if(!messageData) {
      return;
    }

    if(messageData.ActionName !== 'deeplink') {
      return;
    }


    if(!messageData.data) {
      this.services.logger.error(`Received deep link post message action with ${messageData.data} data`);
      return;
    }

    let deepLinkJsonObject = null;

    try {
      deepLinkJsonObject = JSON.parse(messageData.data);
    } catch (err) {
      this.services.logger.error(`Failed to parse deep link object from post message: ${messageData.data}`, err);
      return;
    }

    await this._waitForAutologin();

    let action = this.services.deepLinkActionsFactory.createActionFromDeepLinkObject(deepLinkJsonObject);

    if(action) {
      this.services.logger.debug('Execute deep link object in _executeDeepLinkFromPostMessage: ' + messageData.data);
      return this._executeAction(action);
    } else {
      this.services.logger.error(`Received post message with invalid deep link object ${messageData.data}`);
    }

  }
}

